import { ReactTFC } from "../../utils/fp/missing-ts-parts";
import React from "react";
import { css } from "@emotion/react";
import { CSS_MQ } from "../../styles/breakpoints";
import cn from "classnames";
import { ContentContainer } from "../../components/_shared-components/arrangement/Container";
import { nonEmpty } from "../../utils/fp/small-fp-utils";
import { PdpNavigationBar } from "./pdp-navigation-bar";
import { PDPBarSection } from "./pdp-bar-section";
import { Spacer } from "../../components/_shared-components/arrangement/Space";
import { useVisibility } from "../../hooks/use-visibility/use-visibility";
import { CSS_COLORS } from "../../styles/styles-css-variables";
import type { TranslatedValue } from "@swo/storefront";

export type PDPSections = Array<
  | {
      label: TranslatedValue;
      anchor?: string;
      node: React.ReactNode;
      hidden?: boolean;
    }
  | {
      label: TranslatedValue;
      anchor: string;
      mobileHidden: boolean;
      hidden?: boolean;
    }
>;

type Props = {
  headerState: "pinned" | "unpinned" | "unfixed" | null;
  sections: PDPSections;
  Button: React.ReactNode;
  children?: React.ReactNode;
};

export const PDPSectionsWithNavigation: ReactTFC<Props> = ({ Button, children, headerState, sections }) => {
  const { onVisibilityChange, visibility } = useVisibility(sections);

  return (
    <div
      className="u-relative"
      css={css`
        --g: 8px;
        ${CSS_MQ.atLeastMd} {
          --g: 20px;
        }
        ${CSS_MQ.atLeastLg} {
          --g: 30px;
        }
      `}
    >
      <Spacer size={"md"} />

      <div className={cn(headerState)} css={wrapperStyles}>
        <ContentContainer>
          <PdpNavigationBar sections={sections} Button={Button} visibility={visibility} />
        </ContentContainer>
      </div>

      {sections
        .map((section, idx) => ("node" in section ? { idx, section } : undefined))
        .filter(nonEmpty)
        .map(({ section, idx }) => (
          <PDPBarSection
            key={(section.label.key || idx.toString()) + idx}
            label={section.label}
            node={section.node}
            onVisibilityChange={onVisibilityChange(idx)}
            anchor={section.anchor}
          />
        ))}

      {children}
    </div>
  );
};

const wrapperStyles = css`
  background-color: ${CSS_COLORS.White};

  --top: 0px;
  transition: top 200ms linear;
  z-index: 2;

  &.unpinned {
    --top: 0px;
  }

  &.unfixed {
    --top: 0px;
  }

  &.pinned {
    --top: var(--header-pinned-height);
  }

  position: sticky;
  top: calc(var(--top));
`;
